import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClockIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useAuditoriaEntidades, useAuditoriaTimeline } from '../../hooks/useAuditoria';
import { inputIris } from '../ui/iris';
import { DialogoEvento } from './DialogoEvento';
import { corPorPrefixo } from '../../utils/corAuditoria';
import { fmtDataHora } from '../../utils/formatters';
import type { AuditoriaItem } from '../../types/auditoria';

/**
 * Aba "Timeline" — tudo que aconteceu com UMA entidade, em ordem cronológica.
 * Chega por deep-link (`?aba=timeline&entidade=…&id=…`, atalho "Timeline" da
 * linha na aba Eventos) ou preenchida à mão pelo seletor abaixo.
 *
 * Filtros ficam sincronizados na URL (o próprio propósito da aba é o link ser
 * colável) — diferente de Eventos, cujo estado é aceito perder ao trocar de
 * aba (decisão da Task 6).
 */
export const AbaTimeline: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [entidade, setEntidade] = useState(searchParams.get('entidade') ?? '');
  const [id, setId] = useState(searchParams.get('id') ?? '');
  const [itemSelecionado, setItemSelecionado] = useState<AuditoriaItem | null>(null);

  const { data: entidades = [] } = useAuditoriaEntidades();
  const { data: itens, isLoading, error } = useAuditoriaTimeline(entidade, id, 200);

  const atualizarUrl = (novaEntidade: string, novoId: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (novaEntidade) p.set('entidade', novaEntidade); else p.delete('entidade');
      if (novoId) p.set('id', novoId); else p.delete('id');
      return p;
    }, { replace: true });
  };

  // Mesma disciplina do shell pra `?aba=`: entrada desconhecida (link velho,
  // entidade renomeada) não deixa a tela num estado quebrado — limpa assim que
  // o catálogo real carrega, em vez de tentar buscar uma entidade que não existe.
  // Limpa a URL junto (não só o state) — senão o param morto sobrevive na
  // querystring e, ao remontar o componente (ida-e-volta de aba), o `useState`
  // inicial lê o param de novo e desfaz a limpeza (loop).
  useEffect(() => {
    if (entidades.length > 0 && entidade && !entidades.includes(entidade)) {
      setEntidade('');
      atualizarUrl('', id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidades]);

  const aoMudarEntidade = (v: string) => { setEntidade(v); atualizarUrl(v, id); };
  const aoMudarId = (v: string) => { setId(v); atualizarUrl(entidade, v); };

  const filtroCompleto = entidade.trim().length > 0 && id.trim().length > 0;

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="rounded-iris-lg border border-line bg-surface p-2.5">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Entidade</label>
            <select
              value={entidade}
              onChange={(e) => aoMudarEntidade(e.target.value)}
              className={`min-w-[200px] ${inputIris} h-8 text-xs`}
            >
              <option value="">Selecione a entidade</option>
              {entidades.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">ID da entidade</label>
            <input
              type="text"
              value={id}
              onChange={(e) => aoMudarId(e.target.value)}
              placeholder="ex: 01|12345"
              className={`w-40 ${inputIris} h-8 text-xs`}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto rounded-iris-lg border border-line bg-surface p-3">
        {!filtroCompleto ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-tx-3">
            <ClockIcon className="h-10 w-10 text-tx-3 opacity-50" />
            <span>Escolha uma entidade e um ID pra ver a linha do tempo.</span>
          </div>
        ) : isLoading ? (
          <p className="p-6 text-center text-sm text-tx-3">Carregando linha do tempo...</p>
        ) : error ? (
          <p className="p-6 text-center text-sm text-danger">Erro ao carregar a linha do tempo.</p>
        ) : !itens || itens.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-tx-3">
            <ClockIcon className="h-10 w-10 text-tx-3 opacity-50" />
            <span>Nenhum evento de auditoria para {entidade} {id}.</span>
          </div>
        ) : (
          <ol className="relative ml-3 space-y-4 border-l border-line py-1">
            {itens.map((item) => (
              <li key={item.id} className="relative pl-4">
                <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-accent" />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[11px] text-tx-3">{fmtDataHora(item.criadoEm, true)}</span>
                  <span className={`inline-block rounded-iris-sm px-2 py-0.5 text-[10px] font-semibold ${corPorPrefixo(item.tipo)}`}>
                    {item.tipo}
                  </span>
                  <span className="text-xs text-tx">{item.usuario || 'sistema'}</span>
                  <button
                    type="button"
                    onClick={() => setItemSelecionado(item)}
                    className="ml-auto inline-flex items-center gap-1 rounded-iris-sm border border-line px-2 py-0.5 text-[11px] text-tx-2 hover:bg-surface-2 transition-colors"
                  >
                    <EyeIcon className="h-3 w-3" /> Ver JSON
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {itemSelecionado && (
        <DialogoEvento item={itemSelecionado} onFechar={() => setItemSelecionado(null)} />
      )}
    </div>
  );
};

export default AbaTimeline;
